import { useEffect } from 'react'
import { Button } from '@/components/shared/Button/Button'
import LayoutInside from '@/layout/LayoutInside'
import Title from '@/components/shared/Title/Title'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { NoProducts } from '@/components/shared/NoProducts/NoProducts'
import { ProductsGrid } from '@/components/shared/ProductsGrid/ProductsGrid'
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom'
import { useProductsState } from './hooks/useProductsState'
import { useProductsData } from './hooks/useProductsData'
import { ProductTabs } from './partials/ProductTabs'
import { ProductFilters } from './partials/ProductFilters'
import { usePermissions } from '@/auth/PermissionsContext'
import { usePartnerProducts } from './hooks/usePartnerProducts'
import ReferralLinksGrid from './partials/ReferralLinksGrid'

const Products = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()

  const canCreateProduct = hasPermission('products.create_product');
  const canViewByRole = hasPermission('products.view_products_by_role');

  const {
    tab,
    setTab,
    search,
    setSearch,
    status,
    setStatus,
    page,
    setPage,
    debouncedSearch,
  } = useProductsState()

  const { products, isLoading, refetch, isRefetching, error } = useProductsData(
    {
      type: tab,
      currentPage: page,
      query: debouncedSearch,
      status: tab === 'products' ? status : undefined,
      enabled: (tab === 'products' || canViewByRole) && tab !== 'referral_links',
    },
  )

  const canViewPartnerIndications = hasPermission(
    'partner_indications.view_partner_indications',
  );
  const { partnerProducts, hasReferralProducts, isLoading: isLoadingPartnerProducts } =
    usePartnerProducts(canViewPartnerIndications);
  const isReferralTab = tab === 'referral_links';
  const canViewReferralLinks =
    hasReferralProducts && hasPermission('partner_indications.view_partner_indications');

  useEffect(() => {
    if (tab === 'referral_links' && !canViewReferralLinks) {
      setTab('products');
    }
  }, [tab, canViewReferralLinks, setTab]);

  useEffect(() => {
    const shouldSearch =
      debouncedSearch.length >= 3 || debouncedSearch.length === 0
    if (shouldSearch) {
      refetch()
    }
  }, [debouncedSearch, refetch])

  const hasProducts = products?.data?.length > 0

  const getTitle = () => {
    switch (tab) {
      case 'coproducers':
        return t('my_co_producers')
      case 'affiliates':
        return t('my_affiliates')
      case 'referral_links':
        return t('tabReferralLinks')
      default:
        return t('myProducts')
    }
  }

  useEffect(() => {
    if (!canViewByRole && (tab === 'coproducers' || tab === 'affiliates')) {
      setTab('products');
    }
  }, [canViewByRole, tab, setTab]);

  const showCreateButton = tab === 'products' && canCreateProduct
  const showStatusFilter = tab === 'products'

  const handlePageReset = () => setPage(1)

  const getNoProductsVariant = () => {
    if (search.length !== 0 && search.length >= 3) {
      return 'no-results'
    }
    return 'no-products'
  }

  const getNoProductsTitle = () => {
    switch (tab) {
      case 'coproducers':
        return t('common:noCoProducers')
      case 'affiliates':
        return t('noAffiliates')
      default:
        return t('noProducts')
    }
  }

  const getNoProductsDescription = () => {
    switch (tab) {
      case 'coproducers':
        return t('noCoProducersDescription')
      case 'affiliates':
        return t('noAffiliatesDescription')
      default:
        return t('noProductsDescription')
    }
  }

  if (error) {
    return (
      <LayoutInside items={[{ text: t('products'), link: '/products' }]}>
        <div className="text-white min-h-screen">
          <div className="flex items-center justify-center h-64">
            <p className="text-red-500">{error}</p>
          </div>
        </div>
      </LayoutInside>
    )
  }

  return (
    <LayoutInside items={[{ text: t('products'), link: '/products' }]}>
      <div className="text-white min-h-screen">
        <div className="flex flex-col md:flex-row !justify-between gap-4 w-full min-w-0">
          <div className="w-full min-w-0">
            <ProductTabs
              activeTab={tab}
              onTabChange={setTab}
              handleResetSearch={() => setSearch('')}
              hasReferralProducts={hasReferralProducts}
            />
          </div>

          {showCreateButton && (
            <Button
              variant="primary"
              onClick={() => navigate('/products/create-products')}
              data-cy="create-product"
            >
              {t('btnProductor')}
            </Button>
          )}
        </div>

        {isReferralTab ? (
          <>
            <div className="flex flex-col xl:flex-row justify-between mt-10 mb-6 gap-4">
              <Title text={getTitle()} />
            </div>
            <ReferralLinksGrid
              products={partnerProducts}
              isLoading={isLoadingPartnerProducts}
            />
          </>
        ) : (
          <>
            <div className="flex flex-col xl:flex-row justify-between mt-10 mb-6 gap-4">
              <Title text={getTitle()} />

              <ProductFilters
                search={search}
                onSearchChange={setSearch}
                status={status}
                onStatusChange={setStatus}
                showStatusFilter={showStatusFilter}
                onPageReset={handlePageReset}
              />
            </div>

            <TableListCustom
              variant="custom"
              currentPage={page}
              setCurrentPage={setPage}
              data={{
                data: products?.data ?? [],
                meta: products?.meta,
                links: products?.links,
              }}
              isLoadingData={isLoading || isRefetching}
              loadingFallback={
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <Skeleton key={index} className="w-full h-40 rounded-md" />
                  ))}
                </div>
              }
              emptyFallback={
                <NoProducts
                  data-cy={getNoProductsVariant()}
                  variant={getNoProductsVariant()}
                  emptyTitle={getNoProductsTitle()}
                  emptyDescription={getNoProductsDescription()}
                  hasButton={showCreateButton}
                />
              }
            >
              {hasProducts ? (
                <section>
                  <ProductsGrid products={products?.data} />
                </section>
              ) : (
                <></>
              )}
            </TableListCustom>
          </>
        )}
      </div>
    </LayoutInside>
  )
}

export default Products
